import React, {useEffect} from "react";
import {connect} from "react-redux";
import {useQuery} from "@apollo/client";
import {useHistory, useLocation} from "react-router-dom";

import {VisuallyHidden, Box, SkipNavContent, Heading, Text} from "@unibuddy/patron";
import {useTranslation} from "@unibuddy/intl";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import {pageNames, sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";
import checkAllowDelinkedForApplicant from "shared/Chat/utils/checkAllowDelinkedForApplicant";
import {isFeatureEnabled} from "shared/Utils/FeatureUtils";
import InboxQuery from "shared/Inbox/queries/InboxQuery";
import InboxUILegacy from "shared/Inbox/components/InboxUI/InboxUILegacy";
import {rootURL} from "shared/Utils/WhereAmI";
import {SHOW_INBOX_PROMPT} from "shared/constants";
import {useUniversity} from "../../../UniversityProvider/UniversityProvider";
import {MeField} from "../../../../../types/generated";
import {InboxWrapper} from "./InboxWrapper";
import {PromotedMessageList} from "./PromotedMessageList";
import useShowPrompt from "../../../Prompt/hooks/useShowPrompt";
import {RecommendationPrompt} from "../../../Recommendations/components/RecommendationPrompt/RecommendationPrompt";
import {RECOMMENDATION_PAGE_BATCH_SIZE} from "../../../Recommendations/RecommendationsProvider/RecommendationsProvider";
import {ProspectStageLocation} from "../../../Chat/components/PopupQuestions/prospectStages";
import {IntlNamespaces} from "../../../../../translations/types";
import {INTL_INBOX} from "../../../../../translations/inbox";
import {DownloadUnibuddyAppBanner} from "../../../DownloadUnibuddyApp/DownloadUnibuddyAppBanner";

type AuthState = {
    me: MeField;
};
type StateProps = {
    authState: AuthState;
};

const ApplicantInbox = ({authState, isExternal = false}) => {
    const history = useHistory();
    const location = useLocation();
    const university = useUniversity();

    const {trackEvent} = useAnalytics();
    const {t} = useTranslation(IntlNamespaces.INBOX);
    const title: string = t(INTL_INBOX.key, INTL_INBOX.defaultValue);
    const meAuthState = authState.me;
    const allowDelinked = checkAllowDelinkedForApplicant(university?.slug, meAuthState);
    const isDegreeTypesEnabled = isFeatureEnabled("enableDegreeTypes", university);

    const {loading, data, error} = useQuery(InboxQuery, {
        variables: {
            universitySlug: university.slug,
            allowDelinked,
            isDegreeTypesEnabled,
        },
        fetchPolicy: "cache-and-network",
    });

    const {
        loading: promptLoading,
        data: promptData,
        error: promptError,
        showPrompt,
    } = useShowPrompt(SHOW_INBOX_PROMPT);

    useEffect(() => {
        if (!promptLoading && (promptData || promptError)) {
            trackEvent(sharedEvents.inboxViewed.name, {
                universityId: university.id,
                universityName: university.name,
                universitySlug: university.slug,
                isRecommendationPromptShown: showPrompt,
            });
        }
    }, [university, showPrompt, trackEvent, promptLoading, promptData, promptError]);

    if (loading || promptLoading) {
        return <Text>Loading...</Text>;
    }

    if (error) return <Text>There was an error loading your inbox. Try again later.</Text>;

    const onOpenChat = (inboxItemId?: string) => {
        const baseURL: string = rootURL(location.pathname);
        history.push(`${baseURL}/inbox/${inboxItemId}`);
    };
    return (
        <Box minH={300} maxW={808} marginBottom="small">
            <VisuallyHidden>
                <Heading level="1">{title}</Heading>
            </VisuallyHidden>
            <Box>
                <DownloadUnibuddyAppBanner page={pageNames.inbox} />
                <SkipNavContent id={"content"}>
                    <PromotedMessageList />
                </SkipNavContent>

                <InboxWrapper>
                    <InboxUILegacy
                        chatGroups={data?.applicantInbox?.chatGroups}
                        onOpenChat={onOpenChat}
                        onOpenUniversityChat={onOpenChat}
                        loading={loading}
                        showOnlineStatus={false}
                        university={university}
                        isExternal={isExternal}
                        error={error}
                    />
                </InboxWrapper>
                {showPrompt ? (
                    <Box
                        borderColor="#e4e8eb"
                        borderWidth={1}
                        borderRadius="xxsmall"
                        marginTop="small"
                    >
                        <RecommendationPrompt
                            batchSize={RECOMMENDATION_PAGE_BATCH_SIZE}
                            showPopup={false}
                            location={ProspectStageLocation.WIDGET_INBOX}
                        />
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
};

const mapStateToProps = (state: StateProps) => ({
    authState: state.authState,
});

export default connect(mapStateToProps)(ApplicantInbox);
